/**
 * Client gọi API — docs/04-dac-ta-api.md §4.1.
 *
 * - Luôn gửi cookie (`credentials: 'include'`), phiên nằm ở cookie HttpOnly.
 * - Tự đính kèm `X-CSRF-Token` cho request thay đổi trạng thái (docs/07 §7.7).
 * - Đổi envelope lỗi `{ error: { code, message, details } }` thành `ApiError`
 *   để mọi màn hình xử lý lỗi theo cùng một cách.
 */

import { useMemo, useState } from 'react';
import type { ApiMeta, ErrorCode, FieldError } from '@dnshub/shared';
import { dichNgoaiReact } from './i18n';

export const API_PREFIX = '/api/v1';

/**
 * ── Phiên hết hạn giữa lúc đang dùng ────────────────────────────────────
 *
 * `RequireAuth` chỉ chuyển hướng khi `user === null`, mà `user` chỉ được đọc
 * lại lúc **nạp trang**. Nếu phiên chết trong lúc một tab đang mở (token 15
 * phút, hoặc chạm ngưỡng `SESSION_IDLE_DAYS` / `SESSION_ABSOLUTE_DAYS`) thì
 * `user` vẫn còn trong state React: mọi liên kết vẫn vào được, mỗi lời gọi trả
 * `401`, và màn hình hiện `ErrorState` kèm nút "Thử lại" — bấm bao nhiêu lần
 * cũng `401`. Người dùng không có lối về trang đăng nhập ngoài F5.
 *
 * Nên chốt đặt ở **đây**, chỗ duy nhất mọi lời gọi đi qua, thay vì ở từng màn.
 * `AuthProvider` lắng nghe sự kiện này và xoá `user`, `RequireAuth` lo phần
 * chuyển hướng — client HTTP không cần biết gì về router.
 *
 * KHÔNG phát cho các đường xác thực: `401` ở đó là **câu trả lời**, không phải
 * phiên hỏng — `/auth/me` trả `401` khi chưa đăng nhập (đúng luồng khởi động),
 * và `/auth/login` trả `401` khi sai mật khẩu.
 */
export const SU_KIEN_PHIEN_HET = 'dnshub:phien-het-han';

const DUONG_XAC_THUC = ['/auth/me', '/auth/login', '/auth/logout', '/auth/refresh', '/auth/2fa/'];

function laDuongXacThuc(path: string): boolean {
  return DUONG_XAC_THUC.some((p) => path.startsWith(p));
}

/**
 * Làm mới phiên bằng refresh cookie (OQ-232).
 *
 * Gộp mọi lời gọi đang chờ vào **một** yêu cầu: khi một màn bắn năm truy vấn
 * song song và cả năm cùng gặp 401, năm lời gọi `/auth/refresh` đồng thời sẽ
 * xoay vòng token của nhau — bốn cái sau dùng token đã bị thay và hỏng.
 */
let dangLamMoi: Promise<boolean> | null = null;

async function thuLamMoiPhien(): Promise<boolean> {
  dangLamMoi ??= (async () => {
    try {
      const res = await fetch(`${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': readCookie('csrf_token') },
        credentials: 'include',
      });
      return res.ok;
    } catch {
      /* Mất mạng thì chưa kết luận phiên chết — để lời gọi gốc báo lỗi mạng. */
      return false;
    } finally {
      dangLamMoi = null;
    }
  })();

  return dangLamMoi;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Lỗi theo từng trường của VALIDATION_ERROR (docs/04 §4.1). */
  get fields(): FieldError[] {
    const raw = this.details?.['fields'];
    return Array.isArray(raw) ? (raw as FieldError[]) : [];
  }

  fieldMessage(field: string): string | undefined {
    return this.fields.find((f) => f.field === field)?.message;
  }

  /**
   * Câu đáng hiện cho người dùng — OQ-322.
   *
   * `message` của `VALIDATION_ERROR` luôn là câu chung *"Dữ liệu không hợp lệ."*;
   * lý do thật nằm trong `details.fields[]`. Màn nào chỉ hiện `message` thì người
   * dùng đọc được đúng một câu không nói gì, và phải tự đoán — chủ dự án đã mất
   * công đoán đúng như vậy hai lần (OQ-321).
   *
   * Ưu tiên chi tiết đầu tiên. Có lặp lại với dòng đỏ ở ô nhập thì chấp nhận:
   * lặp một câu đúng vẫn hơn hiện một câu rỗng — và với những lỗi KHÔNG thuộc ô
   * nào (captcha, email đã tồn tại, hệ thống đang đóng đăng ký) thì đây là chỗ
   * duy nhất người dùng đọc được lý do.
   */
  get moTaRoRang(): string {
    return this.fields[0]?.message ?? this.message;
  }

  is(code: ErrorCode | 'INTERNAL_ERROR'): boolean {
    return this.code === code;
  }
}

function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

/**
 * Đổi MỌI lỗi thành `ApiError` để màn hình luôn có gì đó hiện ra.
 *
 * Mẫu `err instanceof ApiError ? err : null` bỏ lọt đúng nhóm lỗi tệ nhất:
 * `fetch` ném `TypeError` khi mất mạng hoặc máy chủ chết giữa chừng, và khi ấy
 * `setError(null)` làm màn hình **đứng im** — nút vừa hiện "Đang xử lý" xong
 * quay về trạng thái cũ, không một dòng chữ nào.
 *
 * Đã xảy ra thật (OQ-220): máy chủ chết trong lúc quản trị viên bấm "Tạo tài
 * khoản"; hộp thoại đứng nguyên và không cách nào biết vì sao.
 *
 * `status = 0` phân biệt "chưa tới được máy chủ" với mọi mã HTTP thật.
 */
export function thanhApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  return new ApiError(0, 'NETWORK_ERROR', dichNgoaiReact('error.network'));
}

/**
 * Thông báo lỗi của máy chủ phải BIẾN MẤT khi người dùng sửa ô nhập (OQ-223).
 *
 * *"Mật khẩu này đã xuất hiện trong các vụ lộ dữ liệu"*, *"Tên miền đã tồn
 * tại"*… đều là phán quyết về **giá trị cũ**. Giữ chúng lại sau khi người dùng
 * đã gõ khác đi là khẳng định một điều **chưa ai kiểm** — và người đọc không có
 * cách nào biết là không phải.
 *
 * Gắn vào phần tử BỌC thay vì từng ô: sự kiện `change` nổi bọt trong React, nên
 * một handler ở đây phủ mọi `<input>`, `<select>`, `<textarea>` bên trong. 50 ô
 * trên 11 màn chỉ cần 11 chỗ gắn, và ô thêm sau này tự được phủ mà không ai
 * phải nhớ.
 *
 * ```tsx
 * const { error, setError, xoaLoiKhiGo } = useLoiApi();
 * <div {...xoaLoiKhiGo}> … </div>
 * ```
 */
export function useLoiApi(): {
  error: ApiError | null;
  setError: (e: ApiError | null) => void;
  xoaLoiKhiGo: { onChange: () => void };
} {
  const [error, setError] = useState<ApiError | null>(null);
  const xoaLoiKhiGo = useMemo(
    () => ({
      onChange: (): void => setError((cu) => (cu === null ? cu : null)),
    }),
    [],
  );
  return { error, setError, xoaLoiKhiGo };
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Gửi FormData thay vì JSON (tải ảnh đại diện). */
  form?: FormData;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await apiWithMeta<T>(path, options)).data;
}

/**
 * Như `apiRequest` nhưng giữ lại cả `meta` phân trang của docs/04 §4.1
 * (`{ page, perPage, total }`) — cần cho bảng Người dùng và Nhật ký.
 */
export async function apiWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: ApiMeta | null }> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (UNSAFE.has(method)) headers['X-CSRF-Token'] = readCookie('csrf_token');

  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const init: RequestInit = { method, headers, credentials: 'include' };
  if (body !== undefined) init.body = body;
  if (options.signal) init.signal = options.signal;

  let res = await fetch(`${API_PREFIX}${path}`, init);

  /*
   * OQ-232 — access token chỉ sống 15 phút, refresh cookie sống tới 30 ngày.
   * Không có đoạn này thì "Ghi nhớ đăng nhập 30 ngày" là lời hứa suông: hết 15
   * phút là văng ra màn đăng nhập, bất kể người dùng đã tick gì.
   *
   * Chỉ thử **một lần** và chỉ khi lời gọi không phải chính đường xác thực —
   * thử lại vòng hai là tự tạo vòng lặp vô hạn khi phiên đã chết thật.
   */
  if (res.status === 401 && !laDuongXacThuc(path)) {
    const lamMoi = await thuLamMoiPhien();
    if (lamMoi) {
      if (UNSAFE.has(method)) headers['X-CSRF-Token'] = readCookie('csrf_token');
      res = await fetch(`${API_PREFIX}${path}`, init);
    }
  }

  if (res.status === 204) return { data: undefined as T, meta: null };

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    const err = (payload['error'] ?? {}) as {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
    };

    if (res.status === 401 && !laDuongXacThuc(path)) {
      window.dispatchEvent(new CustomEvent(SU_KIEN_PHIEN_HET));
    }

    throw new ApiError(
      res.status,
      err.code ?? 'INTERNAL_ERROR',
      err.message ?? dichNgoaiReact('error.unexpected'),
      err.details,
    );
  }

  return {
    data: (payload['data'] ?? null) as T,
    meta: (payload['meta'] as ApiMeta | undefined) ?? null,
  };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  /*
   * PUT — them o OQ-337.
   *
   * `UNSAFE` da liet ke PUT tu dau nen header CSRF di kem san; thieu chi la ham
   * tien ich, vi cho toi gio moi thao tac sua deu la PATCH. Cau hinh ingress cua
   * tunnel la truong hop dau tien co ngu nghia THAY TOAN BO: danh sach gui len
   * la danh sach cuoi cung, khong phai mot phan de tron vao cai cu.
   */
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, form: FormData) => apiRequest<T>(path, { method: 'POST', form }),
};

/**
 * Bảo đảm đã có cookie `csrf_token` trước khi gửi request ghi đầu tiên.
 * Server phát cookie này ở mọi request an toàn (docs/07 §7.7).
 */
export async function ensureCsrfCookie(): Promise<void> {
  if (readCookie('csrf_token')) return;
  await fetch('/health', { credentials: 'include' });
}
